import { useState } from "react";
import { FileText } from "lucide-react";

import { api, errMsg } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { useToast } from "../../../context/ToastContext.js";
import { FileInput } from "../../../components/ui/file-input.js";
import { Button } from "../../../components/ui/button.js";
import { DeleteIconButton, Field, fmtDate } from "../shared.js";
import type { HrAttachment } from "../types.js";

// Same limits the API enforces (performance.schema.ts); checked here first so
// HR gets the message before anything is uploaded.
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt";
const ALLOWED_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
  "text/plain",
]);

function fmtSize(bytes: number | null): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Optional files HR attaches next to the HR comments. Uploads go straight to
// S3 (presigned PUT), then the API confirms the object landed.
export default function HrAttachments({
  reviewId,
  attachments,
  disabled,
  onChanged,
}: {
  reviewId: string;
  attachments: HrAttachment[];
  disabled: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [uploading, setUploading] = useState<string | null>(null);

  async function upload(file: File | null) {
    if (!file) return;
    if (!ALLOWED_TYPES.has(file.type)) {
      toast.show("Only PDF, Word, Excel, PNG, JPG or text files can be attached", "error");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.show("Files must be 10 MB or smaller", "error");
      return;
    }
    setUploading(file.name);
    try {
      const { data } = await api.post<{ attachmentId: string; uploadUrl: string }>(
        `/performance/reviews/${reviewId}/hr-attachments`,
        { fileName: file.name, mimeType: file.type, sizeBytes: file.size },
      );
      const put = await fetch(data.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
      if (!put.ok) throw new Error("The file could not be uploaded to storage");
      await api.post(`/performance/reviews/${reviewId}/hr-attachments/${data.attachmentId}/confirm`);
      toast.show("File attached", "success");
      onChanged();
    } catch (e) {
      logError("HrAttachments.upload", e);
      toast.show(errMsg(e) ?? (e instanceof Error ? e.message : "Could not attach the file"), "error");
    } finally {
      setUploading(null);
    }
  }

  async function open(att: HrAttachment) {
    // Open the tab synchronously so the popup blocker allows it, then point it
    // at the short-lived download link once it arrives.
    const tab = window.open("", "_blank");
    try {
      const { data } = await api.get<{ url: string }>(
        `/performance/reviews/${reviewId}/hr-attachments/${att.id}/url`,
      );
      if (tab) tab.location.href = data.url;
      else window.location.href = data.url;
    } catch (e) {
      tab?.close();
      logError("HrAttachments.open", e);
      toast.show(errMsg(e) ?? "Could not open the file", "error");
    }
  }

  async function remove(att: HrAttachment) {
    if (!window.confirm(`Remove "${att.fileName}"?`)) return;
    try {
      await api.delete(`/performance/reviews/${reviewId}/hr-attachments/${att.id}`);
      toast.show("File removed", "success");
      onChanged();
    } catch (e) {
      logError("HrAttachments.remove", e);
      toast.show(errMsg(e) ?? "Could not remove the file", "error");
    }
  }

  return (
    <Field label="Attachments (optional)" hint="PDF, Word, Excel, PNG, JPG or text, up to 10 MB each.">
      <div className="flex flex-col gap-2">
        {attachments.length > 0 && (
          <ul className="flex flex-col divide-y divide-surface-100 rounded-md border border-surface-200">
            {attachments.map((att) => (
              <li key={att.id} className="flex items-center gap-2 px-3 py-1.5 text-[13px]">
                <FileText size={14} className="shrink-0 text-surface-400" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto min-w-0 justify-start truncate px-1 py-0.5 text-brand-700 hover:underline"
                  onClick={() => void open(att)}
                  title="Open in a new tab"
                >
                  {att.fileName}
                </Button>
                <span className="ml-auto shrink-0 text-[12px] text-surface-400">
                  {fmtSize(att.sizeBytes)} · {fmtDate(att.uploadedAt)}
                </span>
                {!disabled && <DeleteIconButton name={att.fileName} onClick={() => void remove(att)} />}
              </li>
            ))}
          </ul>
        )}
        <div>
          <FileInput
            size="sm"
            accept={ACCEPT}
            disabled={disabled || uploading != null}
            placeholder={uploading ? `Uploading ${uploading}…` : "Attach a file"}
            onChange={(f) => void upload(f)}
          />
        </div>
      </div>
    </Field>
  );
}
